import { ChartController, ChartSelectionPoint, ChartWrapperLike } from '../types/BarChart.types';

const ANNOTATION_TARGET = 'annotation';
const ANNOTATION_TEXT_TARGET = 'annotationtext';
const ANNOTATION_TARGET_PREFIXES = [ANNOTATION_TARGET, ANNOTATION_TEXT_TARGET];
const TILTED_ANNOTATION_GAP_Y = -14;
const MAX_HORIZONTAL_ANNOTATION_DIGITS = 3;
const ANNOTATION_STYLE_RETRY_DELAYS_MS = [0, 50, 120, 250, 400, 700, 1000];
const H_AXIS_LABEL_SELECTOR = 'svg text[id*="hAxis"][id*="label"]';
const DEFAULT_ANNOTATION_COLOR = '#000';
const AXIS_LABEL_SLANT_ANGLE = 60;
const X_AXIS_LABEL_Y_MIN_OFFSET = -4;
const X_AXIS_LABEL_Y_MAX_OFFSET = 40;

type ChartAreaBounds = {
    left: number;
    top: number;
    width: number;
    height: number;
};
type ChartLayoutInterface = {
    getChartAreaBoundingBox?: () => ChartAreaBounds | null | undefined;
};
type ChartWithLayoutInterface = ChartController & {
    getChartLayoutInterface?: () => ChartLayoutInterface | undefined;
};

const isSvgTextElement = (node: Element): node is SVGTextElement => node instanceof SVGTextElement;
const isNonProduction = process.env.NODE_ENV !== 'production';

const normalizeAnnotationColor = (color: string): string =>
    typeof color === 'string' && color.trim() ? color : DEFAULT_ANNOTATION_COLOR;

const resetAnnotationAlignment = (textNode: SVGTextElement) => {
    textNode.setAttribute('text-anchor', 'middle');
    textNode.style.textAnchor = 'middle';
    textNode.setAttribute('dominant-baseline', 'central');
    textNode.style.dominantBaseline = 'central';
    textNode.removeAttribute('dx');
    textNode.setAttribute('dy', '0');
};

const resolveToComputedColor = (color: string): string => {
    const safeColor = normalizeAnnotationColor(color);
    if (typeof window === 'undefined' || typeof document === 'undefined' || !document.body) {
        return safeColor.toLowerCase();
    }
    const probe = document.createElement('span');
    probe.style.color = safeColor;
    probe.style.display = 'none';
    document.body.appendChild(probe);
    const computedColor = (window.getComputedStyle(probe).color || '').toLowerCase();
    document.body.removeChild(probe);
    return computedColor || safeColor.toLowerCase();
};

const isAnnotationNode = (textNode: SVGTextElement, annotationColor: string, annotationComputedColor: string): boolean => {
    const id = textNode.getAttribute('id') || '';
    const fill = (textNode.getAttribute('fill') || '').toLowerCase();
    const styleFill = (textNode.style.fill || '').toLowerCase();
    const computedFill = typeof window !== 'undefined' ? (window.getComputedStyle(textNode).fill || '').toLowerCase() : '';
    const normalizedAnnotationColor = annotationColor.toLowerCase();
    const isAnnotationId = id.startsWith(ANNOTATION_TARGET) || id.startsWith(ANNOTATION_TEXT_TARGET);
    const isAnnotationBlue =
        fill === normalizedAnnotationColor ||
        fill === annotationComputedColor ||
        styleFill === normalizedAnnotationColor ||
        styleFill === annotationComputedColor ||
        computedFill === normalizedAnnotationColor ||
        computedFill === annotationComputedColor;

    return isAnnotationId || isAnnotationBlue;
};

export const normalizeTargetId = (targetID?: string, selectedPoint?: ChartSelectionPoint) => {
    if (!targetID) {
        return '';
    }

    const [prefix, series, row] = targetID.split('#');
    const isAnnotationTarget = ANNOTATION_TARGET_PREFIXES.includes(prefix);
    if (
        isAnnotationTarget &&
        selectedPoint &&
        Number.isInteger(selectedPoint.row) &&
        Number.isInteger(selectedPoint.column) &&
        Number(selectedPoint.column) > 0
    ) {
        const seriesIndex = Math.floor((Number(selectedPoint.column) - 1) / 2);
        return `bar#${seriesIndex}#${selectedPoint.row}`;
    }

    if (isAnnotationTarget) {
        const seriesNumber = parseInt(series, 10);
        const rowNumber = parseInt(row, 10);
        if (Number.isInteger(seriesNumber) && Number.isInteger(rowNumber)) {
            return `bar#${seriesNumber}#${rowNumber}`;
        }
    }

    return targetID;
};

export const shouldTiltAnnotation = (annotationText: string): boolean => {
    const normalizedValue = annotationText?.trim().replace(/,/g, '');
    if (!normalizedValue) {
        return false;
    }

    const integerPart = normalizedValue.replace(/^[+-]/, '').split('.')[0];
    const digitCount = (integerPart.match(/\d/g) || []).length;
    if (digitCount === 0) {
        return false;
    }

    return digitCount > MAX_HORIZONTAL_ANNOTATION_DIGITS;
};

export const applyAnnotationCursor = (chartWrapper: ChartWrapperLike, annotationColor: string): boolean => {
    const chartContainer = chartWrapper?.getContainer?.();
    if (!chartContainer) {
        return false;
    }

    const safeAnnotationColor = normalizeAnnotationColor(annotationColor);
    const annotationComputedColor = resolveToComputedColor(safeAnnotationColor);
    const annotationNodes = chartContainer.querySelectorAll('svg text');
    let hasUpdatedNode = false;
    annotationNodes.forEach((node: Element) => {
        if (!isSvgTextElement(node)) {
            return;
        }
        const textNode = node;
        if (isAnnotationNode(textNode, safeAnnotationColor, annotationComputedColor)) {
            textNode.style.cursor = 'pointer';
            hasUpdatedNode = true;
        }
    });
    return hasUpdatedNode;
};

export const applyAnnotationTilt = (chartWrapper: ChartWrapperLike, annotationColor: string): boolean => {
    const chartContainer = chartWrapper?.getContainer?.();
    if (!chartContainer) {
        return false;
    }

    const safeAnnotationColor = normalizeAnnotationColor(annotationColor);
    const annotationComputedColor = resolveToComputedColor(safeAnnotationColor);
    const annotationNodes = chartContainer.querySelectorAll('svg text');
    let hasUpdatedNode = false;
    annotationNodes.forEach((node: Element) => {
        if (!isSvgTextElement(node)) {
            return;
        }
        const textNode = node;
        if (!isAnnotationNode(textNode, safeAnnotationColor, annotationComputedColor)) {
            return;
        }

        if (!shouldTiltAnnotation(textNode.textContent || '')) {
            textNode.removeAttribute('transform');
            resetAnnotationAlignment(textNode);
            hasUpdatedNode = true;
            return;
        }

        const rotateX = Number(textNode.getAttribute('x'));
        const rotateY = Number(textNode.getAttribute('y'));

        if (Number.isFinite(rotateX) && Number.isFinite(rotateY)) {
            textNode.setAttribute(
                'transform',
                `translate(0 ${TILTED_ANNOTATION_GAP_Y}) rotate(-50 ${rotateX} ${rotateY})`,
            );
        } else {
            textNode.setAttribute('transform', 'rotate(-35)');
        }
        resetAnnotationAlignment(textNode);
        hasUpdatedNode = true;
    });
    return hasUpdatedNode;
};

export const scheduleAnnotationStyling = (
    chartWrapper: ChartWrapperLike,
    annotationColor: string,
): (() => void) => {
    const timeoutIds: ReturnType<typeof setTimeout>[] = [];
    let hasSucceeded = false;
    ANNOTATION_STYLE_RETRY_DELAYS_MS.forEach((delay, index) => {
        const timeoutId = setTimeout(() => {
            if (hasSucceeded) {
                return;
            }
            const hasCursorUpdate = applyAnnotationCursor(chartWrapper, annotationColor);
            const hasTiltUpdate = applyAnnotationTilt(chartWrapper, annotationColor);
            if (hasCursorUpdate || hasTiltUpdate) {
                hasSucceeded = true;
                timeoutIds.slice(index + 1).forEach(id => clearTimeout(id));
            }
        }, delay);
        timeoutIds.push(timeoutId);
    });

    return () => {
        timeoutIds.forEach(timeoutId => clearTimeout(timeoutId));
    };
};

export const applyXAxisLabelSlant = (
    chartWrapper: ChartWrapperLike,
    slantAngle: number = AXIS_LABEL_SLANT_ANGLE,
) : boolean => {
    try {
        const chartContainer = chartWrapper?.getContainer?.();
        if (!chartContainer) {
            return false;
        }

        const chart = chartWrapper?.getChart?.();
        if (!chart) {
            return false;
        }

        const chartWithLayout = chart as ChartWithLayoutInterface;
        const chartLayout = chartWithLayout?.getChartLayoutInterface?.();
        const chartArea: ChartAreaBounds | null | undefined = chartLayout?.getChartAreaBoundingBox?.();
        const chartAreaBottom =
            chartArea && Number.isFinite(chartArea.top) && Number.isFinite(chartArea.height)
                ? chartArea.top + chartArea.height
                : null;
        const chartAreaLeft = chartArea && Number.isFinite(chartArea.left) ? chartArea.left : null;
        const chartAreaRight =
            chartAreaLeft !== null && chartArea && Number.isFinite(chartArea.width)
                ? chartAreaLeft + chartArea.width
                : null;

        const allSvgTextNodes = Array.from(chartContainer.querySelectorAll('svg text'));
        const xAxisLabelsByPosition = allSvgTextNodes.filter(node => {
            if (!isSvgTextElement(node)) {
                return false;
            }

            const id = (node.getAttribute('id') || '').toLowerCase();
            if (id.startsWith(ANNOTATION_TARGET) || id.startsWith(ANNOTATION_TEXT_TARGET)) {
                return false;
            }

            if (chartAreaBottom === null || chartAreaLeft === null || chartAreaRight === null) {
                return false;
            }

            const x = Number(node.getAttribute('x'));
            const y = Number(node.getAttribute('y'));
            if (!Number.isFinite(x) || !Number.isFinite(y)) {
                return false;
            }

            const withinXAxisBand =
                y >= chartAreaBottom + X_AXIS_LABEL_Y_MIN_OFFSET && y <= chartAreaBottom + X_AXIS_LABEL_Y_MAX_OFFSET;
            const withinChartWidth = x >= chartAreaLeft && x <= chartAreaRight;
            return withinXAxisBand && withinChartWidth;
        });

        const xAxisLabels = xAxisLabelsByPosition.length > 0
            ? xAxisLabelsByPosition
            : (() => {
                if (isNonProduction) {
                    console.debug('Falling back to selector-based x-axis label detection', {
                        chartAreaBottom,
                        chartAreaLeft,
                        chartAreaRight,
                    });
                }
                return Array.from(chartContainer.querySelectorAll(H_AXIS_LABEL_SELECTOR));
            })();
        if (xAxisLabels.length === 0) {
            if (isNonProduction) {
                console.warn('No x-axis labels found for slanting');
            }
            return false;
        }

        let hasUpdatedNode = false;
        xAxisLabels.forEach((node: Element) => {
            try {
                if (!isSvgTextElement(node)) {
                    return;
                }

                const xAxisLabel = node;
                const rotateX = Number(xAxisLabel.getAttribute('x'));
                const rotateY = Number(xAxisLabel.getAttribute('y'));

                xAxisLabel.setAttribute('text-anchor', 'end');
                xAxisLabel.style.textAnchor = 'end';
                xAxisLabel.setAttribute('dominant-baseline', 'central');
                xAxisLabel.style.dominantBaseline = 'central';

                if (Number.isFinite(rotateX) && Number.isFinite(rotateY)) {
                    xAxisLabel.setAttribute('transform', `rotate(-${slantAngle} ${rotateX} ${rotateY})`);
                } else {
                    xAxisLabel.setAttribute('transform', `rotate(-${slantAngle})`);
                }
                hasUpdatedNode = true;
            } catch (labelError) {
                console.warn('Failed to apply slant to an x-axis label', labelError);
            }
        });
        return hasUpdatedNode;
    } catch (error) {
        console.error('Failed to apply x-axis label slant', error);
        return false;
    }
};

/**
 * Schedules repeated slant attempts and returns cleanup for pending timers.
 */
export const scheduleXAxisLabelSlant = (
    chartWrapper: ChartWrapperLike,
    slantAngle: number = AXIS_LABEL_SLANT_ANGLE,
): (() => void) => {
    const timeoutIds: ReturnType<typeof setTimeout>[] = [];
    let hasSucceeded = false;
    ANNOTATION_STYLE_RETRY_DELAYS_MS.forEach((delay, index) => {
        const timeoutId = setTimeout(() => {
            if (hasSucceeded) {
                return;
            }
            const hasAppliedSlant = applyXAxisLabelSlant(chartWrapper, slantAngle);
            if (hasAppliedSlant) {
                hasSucceeded = true;
                timeoutIds.slice(index + 1).forEach(id => clearTimeout(id));
            }
        }, delay);
        timeoutIds.push(timeoutId);
    });

    return () => {
        timeoutIds.forEach(timeoutId => clearTimeout(timeoutId));
    };
};

export const setHoverInteraction = (
    chartWrapper: ChartWrapperLike,
    chart: ChartController,
    event: ChartSelectionPoint,
    annotationColor: string,
) => {
    const chartContainer = chartWrapper?.getContainer?.();
    if (!chartContainer) {
        return;
    }

    const isDataPointHover =
        Number.isInteger(event?.row) && Number.isInteger(event?.column) && Number(event.column) > 0;
    chartContainer.style.cursor = isDataPointHover ? 'pointer' : 'default';

    const reapplyChartTextStyling = () => {
        applyAnnotationTilt(chartWrapper, annotationColor);
        applyXAxisLabelSlant(chartWrapper);
    };

    const scheduleReapplyChartTextStyling = () => {
        if (typeof window !== 'undefined' && window.requestAnimationFrame) {
            window.requestAnimationFrame(reapplyChartTextStyling);
            return;
        }
        setTimeout(reapplyChartTextStyling, 0);
    };

    if (!isDataPointHover) {
        chart?.setSelection?.([]);
        scheduleReapplyChartTextStyling();
        return;
    }

    const hoveredColumn = Number(event.column);
    const normalizedColumn = hoveredColumn % 2 === 0 ? hoveredColumn - 1 : hoveredColumn;
    if (normalizedColumn > 0) {
        chart?.setSelection?.([{ row: Number(event.row), column: normalizedColumn }]);
        scheduleReapplyChartTextStyling();
    }
};

export const getBarChartColumns = (data: any[]) => {
    const headerRow = data?.[0] || [];
    return headerRow.flatMap((columnData: string, index: number) => {
        if (index === 0) {
            return [{ type: 'string', label: columnData }];
        }
        return [
            { type: 'number', label: columnData },
            { type: 'string', role: ANNOTATION_TARGET },
        ];
    });
};

export const getBarChartRows = (data: any[]) => {
    const result: any = [];
    data.forEach((dataRow: any[], dataIndex) => {
        if (dataIndex === 0) {
            return;
        }

        const transformedRow: any[] = [dataRow[0]];
        dataRow.slice(1).forEach(value => {
            const pointValue = value === 0 ? null : value;
            transformedRow.push(pointValue);
            transformedRow.push(pointValue === null ? null : String(value));
        });
        result.push(transformedRow);
    });
    return result;
};
